import { SearchInput } from '@/components/searchInput';
import { QuoteStatus } from '../types';
import { PROJECT_TYPE_OPTIONS, REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { Button, DatePicker, Popover, Select, Space, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { IoCloseOutline, IoFunnelOutline } from 'react-icons/io5';
import { STATUS_FILTER_OPTIONS } from '../constants';
import { useAccountSearch } from '../hooks/useFilterSearch';

const { RangePicker } = DatePicker;

interface QuoteFiltersProps {
  currentSearchText: string;
  currentStatus: QuoteStatus | null;
  currentRegion: number | null;
  currentAccount: string | null;
  currentDateRange: [string, string] | null;
  currentProjectType: number | null;
  isRegionAll: boolean;
  userRegion?: number;
  onSearchChange: (searchText: string) => void;
  onStatusChange: (status: QuoteStatus | null) => void;
  onRegionChange: (region: number | null) => void;
  onAccountChange: (accountId: string | null) => void;
  onDateRangeChange: (dateRange: [string, string] | null) => void;
  onProjectTypeChange: (projectType: number | null) => void;
  onApplyFilters: (filters: {
    status: QuoteStatus | null;
    region: number | null;
    account: string | null;
    dateRange: [string, string] | null;
    projectType: number | null;
  }) => void;
  onClearAll: () => void;
}

export const QuoteFilters: React.FC<QuoteFiltersProps> = ({
  currentSearchText,
  currentStatus,
  currentRegion,
  currentAccount,
  currentDateRange,
  currentProjectType,
  isRegionAll,
  userRegion,
  onSearchChange,
  onStatusChange,
  onRegionChange,
  onAccountChange,
  onDateRangeChange,
  onProjectTypeChange,
  onApplyFilters,
  onClearAll,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [accountSearchText, setAccountSearchText] = useState('');

  // Draft state — only committed to parent when "Apply Filters" is clicked
  const [draftStatus, setDraftStatus] = useState<QuoteStatus | null>(currentStatus);
  const [draftRegion, setDraftRegion] = useState<number | null>(currentRegion);
  const [draftAccount, setDraftAccount] = useState<string | null>(currentAccount);
  const [draftDateRange, setDraftDateRange] = useState<[string, string] | null>(currentDateRange);
  const [draftProjectType, setDraftProjectType] = useState<number | null>(currentProjectType);

  // Prevent body scroll when popover is open
  useEffect(() => {
    if (filterPopoverOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [filterPopoverOpen]);

  // Sync draft from applied values each time the popover opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftStatus(currentStatus);
      setDraftRegion(currentRegion);
      setDraftAccount(currentAccount);
      setDraftDateRange(currentDateRange);
      setDraftProjectType(currentProjectType);
    }
    setFilterPopoverOpen(open);
  };

  // Fetch accounts with optimized search (debounced + $top)
  const { data: accountsData, isLoading: isLoadingAccounts } = useAccountSearch(
    accountSearchText,
    20
  );

  // Convert to select options
  const accountOptions = useMemo(() => {
    return (accountsData?.value || []).map((account) => ({
      label: account.name,
      value: account.accountid,
    }));
  }, [accountsData]);

  // Helper function to get filter label for active filter tags
  const getFilterLabel = (type: string, value: string | number | null) => {
    if (value === null) return null;
    switch (type) {
      case 'status':
        return STATUS_FILTER_OPTIONS.find(opt => opt.value === value)?.label ?? String(value);
      case 'region':
        return REGION_FILTERS_OPTIONS.find(opt => opt.value === value)?.label ?? String(value);
      case 'account':
        return accountOptions.find(opt => opt.value === value)?.label ?? String(value);
      case 'projectType':
        return PROJECT_TYPE_OPTIONS.find(opt => opt.value === value)?.label ?? String(value);
      default:
        return null;
    }
  };

  // Count currently applied filters (not draft)
  const activeFilterCount = [
    currentStatus !== null,
    isRegionAll && currentRegion !== null,
    currentAccount !== null,
    currentDateRange !== null,
    currentProjectType !== null,
  ].filter(Boolean).length;

  const draftDateRangeValue: [Dayjs, Dayjs] | null =
    draftDateRange
      ? [dayjs(draftDateRange[0]), dayjs(draftDateRange[1])]
      : null;

  const handleApplyFilters = () => {
    onApplyFilters({
      status: draftStatus,
      region: draftRegion,
      account: draftAccount,
      dateRange: draftDateRange,
      projectType: draftProjectType,
    });
    setFilterPopoverOpen(false);
  };

  const handleClearAll = () => {
    setDraftStatus(null);
    setDraftRegion(null);
    setDraftAccount(null);
    setDraftDateRange(null);
    setDraftProjectType(null);
    onClearAll();
  };

  return (
    <div className="mb-4">
      <div className="flex gap-3 items-center flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[250px] max-w-[500px]">
          <SearchInput
            placeholder="Search by customer, reference, quote number..."
            onChange={(e) => onSearchChange(e)}
            allowClear
          />
        </div>

        {/* More Filters Button */}
        <div>
          <Popover
            content={
              <div style={{ width: 420, maxHeight: '350px', display: 'flex', flexDirection: 'column' }}>
                {/* Scrollable Filter Container */}
                <div
                  style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: 8,
                    paddingBottom: 16,
                    flex: 1
                  }}
                  className="pr-2"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select status"
                        value={draftStatus === null ? undefined : draftStatus}
                        onChange={(value) => {
                          setDraftStatus(value === undefined || value === null ? null : value as QuoteStatus);
                        }}
                        options={STATUS_FILTER_OPTIONS}
                        allowClear
                      />
                    </div>

                    {/* Region Filter */}
                    {isRegionAll && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Region
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select region"
                          value={draftRegion === null ? undefined : draftRegion}
                          onChange={(value) => {
                            setDraftRegion(value === undefined || value === null ? null : value as number);
                          }}
                          options={REGION_FILTERS_OPTIONS}
                          allowClear
                        />
                      </div>
                    )}

                    {/* Account Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account (Customer)
                      </label>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search and select account"
                        value={draftAccount === null ? undefined : draftAccount}
                        onChange={(value) => {
                          setDraftAccount(value === undefined || value === null ? null : value as string);
                        }}
                        onSearch={(value) => setAccountSearchText(value)}
                        filterOption={false}
                        options={accountOptions}
                        loading={isLoadingAccounts}
                        allowClear
                        notFoundContent={isLoadingAccounts ? 'Loading...' : 'No accounts found'}
                      />
                    </div>

                    {/* Project Type Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Type
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select project type"
                        value={draftProjectType === null ? undefined : draftProjectType}
                        onChange={(value) => {
                          setDraftProjectType(value === undefined || value === null ? null : value as number);
                        }}
                        options={PROJECT_TYPE_OPTIONS}
                        allowClear
                      />
                    </div>

                    {/* Date Range Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quote Date Range
                      </label>
                      <RangePicker
                        style={{ width: '100%' }}
                        value={draftDateRangeValue}
                        onChange={(dates) => {
                          if (!dates || !dates[0] || !dates[1]) {
                            setDraftDateRange(null);
                          } else {
                            setDraftDateRange([
                              dates[0].startOf('day').toISOString(),
                              dates[1].endOf('day').toISOString(),
                            ]);
                          }
                        }}
                        format="DD-MM-YYYY"
                      />
                    </div>
                  </Space>
                </div>

                {/* Footer with Actions */}
                <div
                  className="pt-3 px-2 flex flex-row justify-end"
                  style={{
                    borderTop: '1px solid #f0f0f0',
                    background: 'white',
                    flexShrink: 0
                  }}
                >
                  <Space style={{ width: '100%' }}>
                    {activeFilterCount > 0 && (
                      <Button block onClick={handleClearAll}>
                        Clear All
                      </Button>
                    )}
                    <Button type="primary" block onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                  </Space>
                </div>
              </div>
            }
            title={
              <div className="flex items-center justify-between">
                <span>Filter Options</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs text-gray-500">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
            }
            trigger="click"
            open={filterPopoverOpen}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            overlayStyle={{ zIndex: 1050 }}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          >
            <Button icon={<IoFunnelOutline />} className='h-[44px]'>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </Popover>
        </div>
      </div>

      {/* Active Filters Tags — reflect applied state, close buttons remove immediately */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {currentStatus !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onStatusChange(null)}>
              Status: {getFilterLabel('status', currentStatus)}
            </Tag>
          )}
          {isRegionAll && currentRegion !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onRegionChange(null)}>
              Region: {getFilterLabel('region', currentRegion)}
            </Tag>
          )}
          {currentAccount !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onAccountChange(null)}>
              Account: {getFilterLabel('account', currentAccount)}
            </Tag>
          )}
          {currentProjectType !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onProjectTypeChange(null)}>
              Project Type: {getFilterLabel('projectType', currentProjectType)}
            </Tag>
          )}
          {currentDateRange !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onDateRangeChange(null)}>
              Date: {dayjs(currentDateRange[0]).format('DD-MM-YYYY')} – {dayjs(currentDateRange[1]).format('DD-MM-YYYY')}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            className='text-primary-500'
            icon={<IoCloseOutline />}
            onClick={onClearAll}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuoteFilters;
